import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, Text } from '@beef/ui-kit';
import classNames from 'classnames/bind';
import { MediaBackground } from "../../Media/MediaBackground";
import { useIsMobile } from "../../../hooks/useIsMobile";
import style from "./styles.module.pcss";
var cn = classNames.bind(style);
var CLICK_TIMEOUT = 1000;
var DEFAULT_SLIDE_TIMEOUT = 4000;
var StoryDetail = function StoryDetail(_ref) {
  var _ref$active = _ref.active,
    active = _ref$active === void 0 ? false : _ref$active,
    isSlideInTransition = _ref.isSlideInTransition,
    onDetailClick = _ref.onDetailClick,
    onLeftSlide = _ref.onLeftSlide,
    onRightSlide = _ref.onRightSlide,
    story = _ref.story;
  var _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    videoRef = _useState2[0],
    setVideoRef = _useState2[1];
  var _useState3 = useState(0),
    _useState4 = _slicedToArray(_useState3, 2),
    progressWidth = _useState4[0],
    setProgressWidth = _useState4[1];
  var _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    paused = _useState6[0],
    setPaused = _useState6[1];
  var _useState7 = useState(0),
    _useState8 = _slicedToArray(_useState7, 2),
    activeSlideIndex = _useState8[0],
    setActiveSlideIndex = _useState8[1];
  var lastTouch = useRef(0);
  var isMobile = useIsMobile();
  var activeSlide = story.slides[activeSlideIndex];
  var image = activeSlide.image,
    link = activeSlide.link,
    text = activeSlide.text,
    _activeSlide$textColo = activeSlide.textColor,
    textColor = _activeSlide$textColo === void 0 ? 'white' : _activeSlide$textColo,
    title = activeSlide.title,
    _activeSlide$viewTime = activeSlide.viewTimeout,
    viewTimeout = _activeSlide$viewTime === void 0 ? DEFAULT_SLIDE_TIMEOUT : _activeSlide$viewTime;
  var isVideo = new URL(image).pathname.endsWith('.mp4');
  var handleUserKeyPress = useCallback(function (event) {
    if (!active) return;
    var key = event.key;
    if (key === 'ArrowLeft') {
      if (activeSlideIndex === 0) {
        onLeftSlide === null || onLeftSlide === void 0 ? void 0 : onLeftSlide();
        return;
      }
      setProgressWidth(0);
      setActiveSlideIndex(activeSlideIndex - 1);
    }
    if (key === 'ArrowRight') {
      if (activeSlideIndex === story.slides.length - 1) {
        onRightSlide === null || onRightSlide === void 0 ? void 0 : onRightSlide();
      } else {
        setProgressWidth(0);
        setActiveSlideIndex(function (prevActiveSlideIndex) {
          return prevActiveSlideIndex + 1;
        });
      }
    }
    setPaused(false);
  }, [active, activeSlideIndex, onLeftSlide, onRightSlide, story.slides.length]);
  useEffect(function () {
    window.addEventListener('keydown', handleUserKeyPress);
    return function () {
      window.removeEventListener('keydown', handleUserKeyPress);
    };
  }, [handleUserKeyPress]);
  useEffect(function () {
    if (videoRef) {
      var isPlaying = videoRef.currentTime > 0 && !videoRef.paused && !videoRef.ended && videoRef.readyState > videoRef.HAVE_CURRENT_DATA;
      if (active && (!isPlaying || !paused)) {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        videoRef.play();
      } else {
        videoRef.pause();
        if (!active) {
          videoRef.currentTime = 0;
        }
      }
    }
  }, [active, videoRef, paused]);
  useEffect(function () {
    setProgressWidth(0);
  }, [active]);
  useEffect(function () {
    if (progressWidth >= 100) {
      if (activeSlideIndex === story.slides.length - 1) {
        return onRightSlide === null || onRightSlide === void 0 ? void 0 : onRightSlide();
      }
      setProgressWidth(0);
      setActiveSlideIndex(function (prevActiveSlideIndex) {
        return (prevActiveSlideIndex + 1) % story.slides.length;
      });
    }
    if (paused || !active || !viewTimeout || viewTimeout === 0) {
      return undefined;
    }
    var timeout = 60;
    var timeoutId = setTimeout(function () {
      setProgressWidth(progressWidth + 100 / (viewTimeout / timeout));
    }, timeout);
    return function () {
      return clearTimeout(timeoutId);
    };
  }, [active, viewTimeout, progressWidth, setProgressWidth, activeSlideIndex, onRightSlide, paused, story.slides.length]);
  var showLegend = useMemo(function () {
    if (active) {
      return active;
    }
    return link === null || link === void 0 ? void 0 : link.text;
  }, [active, link === null || link === void 0 ? void 0 : link.text]);
  var handlePause = useCallback(function (val) {
    setPaused(val);
  }, [setPaused]);
  var handleNextSlideClick = useCallback(function (e) {
    var rect = e.currentTarget.getBoundingClientRect();
    var xPosition = e.clientX - rect.left;
    var isLeft = xPosition < rect.width / 2;
    if (isLeft && activeSlideIndex === 0 && story.slides.length > 1) {
      return;
    }
    if (isLeft && activeSlideIndex === 0) {
      onLeftSlide === null || onLeftSlide === void 0 ? void 0 : onLeftSlide();
      return;
    }
    if (story.slides.length === 1) {
      onRightSlide === null || onRightSlide === void 0 ? void 0 : onRightSlide();
      return;
    }
    if (activeSlideIndex === story.slides.length - 1 && !isLeft) {
      onRightSlide === null || onRightSlide === void 0 ? void 0 : onRightSlide();
      return;
    }
    setProgressWidth(0);
    setActiveSlideIndex(function (prevActiveSlideIndex) {
      return prevActiveSlideIndex + (isLeft ? -1 : 1);
    });
    setPaused(false);
  }, [story, activeSlideIndex, onRightSlide, onLeftSlide]);
  var progressBar = [];
  for (var i = 0; i < story.slides.length; i += 1) {
    var isViewed = i < activeSlideIndex;
    var isActiveSlide = i === activeSlideIndex;
    progressBar.push( /*#__PURE__*/React.createElement("div", {
      className: cn('progress__line'),
      key: i,
      "data-t-id": "StoriesV2-StoryDetail"
    }, /*#__PURE__*/React.createElement("div", {
      className: cn('progress__line__content'),
      style: {
        transition: isActiveSlide ? 'width 60ms linear' : 'none',
        // eslint-disable-next-line no-nested-ternary
        width: "".concat(isActiveSlide ? progressWidth : isViewed ? 100 : 0, "%")
      }
    })));
  }
  return /*#__PURE__*/React.createElement("div", {
    className: cn('card-wrap'),
    onClick: handleNextSlideClick,
    onMouseEnter: function onMouseEnter() {
      if (!isMobile && !(isSlideInTransition !== null && isSlideInTransition !== void 0 && isSlideInTransition.current)) {
        handlePause(true);
      }
    },
    onMouseLeave: function onMouseLeave() {
      if (!isMobile) {
        handlePause(false);
      }
    },
    onTouchEnd: function onTouchEnd(e) {
      if (isMobile && +Date.now() - lastTouch.current > CLICK_TIMEOUT) {
        e.preventDefault();
      }
      handlePause(false);
    },
    onTouchStart: function onTouchStart() {
      lastTouch.current = +Date.now();
      handlePause(true);
    }
  }, active && !!viewTimeout && viewTimeout > 0 && /*#__PURE__*/React.createElement("div", {
    className: cn('progress')
  }, progressBar), isVideo ? /*#__PURE__*/React.createElement(MediaBackground, {
    video: {
      autoplay: false,
      onSetVideoRef: function onSetVideoRef(ref) {
        setVideoRef(ref);
      },
      playsinline: true,
      src: image
    }
  }) : /*#__PURE__*/React.createElement(MediaBackground, {
    img: {
      src: image
    }
  }), /*#__PURE__*/React.createElement("div", {
    className: cn('overlay')
  }), showLegend && /*#__PURE__*/React.createElement("div", {
    className: cn('legend', {
      'legend--expanded': active
    })
  }, title && /*#__PURE__*/React.createElement(Text, {
    color: textColor,
    size: active ? 'size2-m' : 'size4-m'
  }, title), active && /*#__PURE__*/React.createElement(React.Fragment, null, text && /*#__PURE__*/React.createElement("div", {
    className: cn('legend__title')
  }, /*#__PURE__*/React.createElement(Text, {
    color: textColor,
    size: "size5-r-l"
  }, text)), link && link.text && link.url && /*#__PURE__*/React.createElement("div", {
    className: cn('legend__link')
  }, /*#__PURE__*/React.createElement(Link, {
    buttonSize: "l",
    buttonVariant: "main",
    href: link.url,
    isFullWidth: true,
    onClick: function onClick(e) {
      e.stopPropagation();
      if (onDetailClick) {
        onDetailClick(e, {
          image: image,
          link: link,
          text: text,
          title: title,
          activeSlideIndex: activeSlideIndex
        });
      }
    },
    variant: "button"
  }, link.text)))));
};
export { StoryDetail };